import React, { useState, useEffect, useMemo } from "react";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import TextInputComp from "../../../components/customer/TextInputComp";
import MediaUploadComp from "../../../components/MediaUploadComp";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useUpdatePetMutation,
  useLazyGetPetsByIdQuery,
} from "../../../redux/actions/Admin/PetsAdmin";

import { PetSellType, PetStatus } from "../../../types/Request/AdminRequest";
import { Loader2, AlertCircle, ArrowLeft, Plus, Trash2, Search, Check } from "lucide-react";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import {
  useLazyGetCategoryQuery,
  useLazyGetSystemDefinedCategoryQuery,
} from "../../../redux/actions/Admin/dashboardAdmin";

interface IVideoAttachment {
  videoUrl: string;
  thumbnailUrl: string;
}

interface ICategoryNode {
  id: number;
  name: string;
  slug?: string;
  parentId?: number | null;
  children?: ICategoryNode[];
  subCategories?: ICategoryNode[];
}

const AdminPetEditScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const petId =
    location.state?.petData?.id ||
    location.state?.petData?.PetId ||
    location.state?.id ||
    location.state?.PetId;

  // RTK Query & Mutation Hooks
  const [
    fetchPetById,
    { data: petFetchedData, isLoading: isFetching, isError: isFetchError },
  ] = useLazyGetPetsByIdQuery();
  const [
    triggerGetFullTree,
    { data: fullTreeResponse, isLoading: isLoadingFullTree },
  ] = useLazyGetCategoryQuery();

  const [
    triggerGetSystemTabs,
    { data: systemTabs },
  ] = useLazyGetSystemDefinedCategoryQuery();

  const [updatePet, { isLoading: isUpdating }] = useUpdatePetMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  const isProcessing = isUpdating || isUploading;

  // --- Core State Parameters ---
  const [microchipId, setMicrochipId] = useState("");
  const [petName, setPetName] = useState("");
  const [breed, setBreed] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState<Option | null>(null);
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState("");
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [weight, setWeight] = useState("");

  // Category Tree Selection States
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedCategoryLabels, setSelectedCategoryLabels] = useState<string[]>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");

  const [isVaccinated, setIsVaccinated] = useState(true);
  const [vaccinationDetails, setVaccinationDetails] = useState("");
  const [isKCIRegistered, setIsKCIRegistered] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  const [price, setPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [bookPrice, setBookPrice] = useState("");

  const [mainImage, setMainImage] = useState("");
  const [subImages, setSubImages] = useState<string[]>([]);
  const [videos, setVideos] = useState<IVideoAttachment[]>([]);

  const [petType, setPetType] = useState<Option | null>(null);
  const [petStatus, setPetStatus] = useState<Option | null>(null);

  const genderOptions: Option[] = [
    { id: "g1", label: "Male", value: "male" },
    { id: "g2", label: "Female", value: "female" },
  ];

  const typeOptions: Option[] = [
    { id: "t1", label: "Buy Pet", value: String(PetSellType.Sell) },
    { id: "t2", label: "Adopt Pet", value: String(PetSellType.Adopt) },
  ];

  const statusOptions: Option[] = [
    { id: "s1", label: "Available", value: String(PetStatus.Available) },
    { id: "s2", label: "Reserved", value: String(PetStatus.Reserved) },
    { id: "s3", label: "Sold", value: String(PetStatus.Sold) },
    { id: "s4", label: "Adopted", value: String(PetStatus.Adopted) },
  ];

  useEffect(() => {
    triggerGetFullTree();
    triggerGetSystemTabs();
    if (petId) {
      fetchPetById(Number(petId));
    } else {
      navigate(-1);
    }
  }, [petId, fetchPetById, triggerGetFullTree, triggerGetSystemTabs, navigate]);

  // Extract raw category tree list
  const treeList: ICategoryNode[] = useMemo(() => {
    const raw = (fullTreeResponse as any)?.data || (fullTreeResponse as any)?.value || fullTreeResponse || [];
    return Array.isArray(raw) ? raw : [];
  }, [fullTreeResponse]);

  // Only expose the Pets category branch for pet tagging.
  // The Pets root is resolved from the same System Defined Categories
  // used by Category Management, so no category ID is hard-coded.
  const petCategoryTree: ICategoryNode[] = useMemo(() => {
    if (treeList.length === 0) return [];

    const tabs = Array.isArray(systemTabs) ? systemTabs : [];
    const petsTab = tabs.find(
      (tab: any) => String(tab?.name || "").trim().toLowerCase() === "pets"
    );

    const petsRootId = petsTab?.id != null ? Number(petsTab.id) : null;

    const findNode = (nodes: ICategoryNode[]): ICategoryNode | null => {
      for (const node of nodes) {
        if (
          (petsRootId !== null && Number(node.id) === petsRootId) ||
          String(node.name || "").trim().toLowerCase() === "pets"
        ) {
          return node;
        }

        const children = node.children || node.subCategories || [];
        const found = findNode(children);
        if (found) return found;
      }
      return null;
    };

    const petsNode = findNode(treeList);
    if (petsNode) return [petsNode];

    // Fallback for APIs that return the system root separately from its descendants.
    if (petsRootId !== null) {
      const directChildren = treeList.filter(
        (node) => Number(node.parentId) === petsRootId
      );

      if (directChildren.length > 0) {
        return directChildren;
      }
    }

    return [];
  }, [treeList, systemTabs]);

  // Hydrate fields when backend response arrives
  useEffect(() => {
    if (petFetchedData) {
      const pet = (petFetchedData as any)?.value?.data || (petFetchedData as any)?.data || petFetchedData;

      setMicrochipId(pet.microchipId || pet.MicrochipId || "");
      setPetName(pet.petName || pet.PetName || "");
      setBreed(pet.breed || pet.Breed || "");

      const rawAge = pet.age || pet.Age || "";
      if (rawAge && String(rawAge).includes("-")) {
        setBirthDate(String(rawAge).split("T")[0]);
      }

      setDescription(pet.description || pet.Description || "");
      setTag(pet.tag || pet.Tag || "");
      setColor(pet.color || pet.Color || "");
      setSize(pet.size || pet.Size || "");
      setWeight(pet.weight ?? pet.Weight ?? "");

      setIsVaccinated(pet.isVaccinated ?? pet.IsVaccinated ?? true);
      setVaccinationDetails(pet.vaccinationDetails || pet.VaccinationDetails || "");
      setIsKCIRegistered(pet.isKCIRegistered ?? pet.IsKCIRegistered ?? false);
      setIsFeatured(pet.isFeatured ?? pet.IsFeatured ?? false);

      setPrice(String(pet.price ?? pet.Price ?? ""));
      setSalePrice(pet.salePrice ?? pet.SalePrice ? String(pet.salePrice ?? pet.SalePrice) : "");
      setBookPrice(pet.bookPrice ?? pet.BookPrice ? String(pet.bookPrice ?? pet.BookPrice) : "");

      setMainImage(pet.productImage || pet.ProductImage || pet.thumbnailUrl || pet.ThumbnailUrl || "");

      // Handle category hydration from categoryIds or Categories array
      if (pet.categoryIds && Array.isArray(pet.categoryIds) && pet.categoryIds.length > 0) {
        setSelectedCategoryIds(pet.categoryIds);
      } else if (pet.categories && Array.isArray(pet.categories)) {
        setSelectedCategoryIds(pet.categories.map((c: any) => c.id || c.Id));
        setSelectedCategoryLabels(pet.categories.map((c: any) => c.name || c.Name));
      } else if (pet.Categories && Array.isArray(pet.Categories)) {
        setSelectedCategoryIds(pet.Categories.map((c: any) => c.id || c.Id));
        setSelectedCategoryLabels(pet.Categories.map((c: any) => c.name || c.Name));
      } else if (pet.category || pet.Category) {
        setSelectedCategoryLabels([pet.category || pet.Category]);
      }

      // Handle Gallery images
      const rawSubImages = pet.subImages || pet.SubImages || [];
      if (Array.isArray(rawSubImages)) {
        setSubImages(rawSubImages.map((i: any) => (typeof i === "string" ? i : i.imageUrl || i.ImageUrl)).filter(Boolean));
      }

      // Handle Videos
      const rawVideos = pet.videos || pet.Videos || [];
      if (Array.isArray(rawVideos)) {
        setVideos(
          rawVideos.map((v: any) => ({
            videoUrl: v.videoUrl || v.VideoUrl || (typeof v === "string" ? v : ""),
            thumbnailUrl: v.thumbnailUrl || v.ThumbnailUrl || pet.productImage || pet.ProductImage || "",
          })).filter((v: any) => v.videoUrl)
        );
      } else if (pet.videoUrl || pet.VideoUrl) {
        setVideos([
          {
            videoUrl: pet.videoUrl || pet.VideoUrl,
            thumbnailUrl: pet.thumbnailUrl || pet.ThumbnailUrl || pet.productImage || pet.ProductImage || "",
          },
        ]);
      }

      // Handle Gender
      const rawGender = pet.gender || pet.Gender || "";
      if (rawGender) {
        const foundG = genderOptions.find((g) => g.label.toLowerCase() === rawGender.toLowerCase());
        setGender(foundG || { id: "g-h", label: rawGender, value: rawGender.toLowerCase() });
      }

      // Handle Pet Type
      const rawType = pet.petType ?? pet.PetType;
      if (rawType !== undefined) {
        const isAdopt = String(rawType).toLowerCase() === "adopt" || Number(rawType) === PetSellType.Adopt;
        setPetType({
          id: "type-hydrate",
          label: isAdopt ? "Adopt Pet" : "Buy Pet",
          value: isAdopt ? String(PetSellType.Adopt) : String(PetSellType.Sell),
        });
      }

      // Handle Pet Status
      const rawStatus = pet.petStatus ?? pet.PetStatus;
      if (rawStatus !== undefined) {
        const matchedStatus = statusOptions.find(
          (s) => s.label.toLowerCase() === String(rawStatus).toLowerCase() || Number(s.value) === Number(rawStatus)
        );
        setPetStatus(matchedStatus || { id: "status-h", label: "Available", value: String(PetStatus.Available) });
      }
    }
  }, [petFetchedData]);

  // Sync category labels from tree if tree loaded after pet details
  useEffect(() => {
    if (selectedCategoryIds.length > 0 && treeList.length > 0 && selectedCategoryLabels.length === 0) {
      const labels: string[] = [];
      const findLabels = (nodes: ICategoryNode[]) => {
        nodes.forEach((n) => {
          if (selectedCategoryIds.includes(n.id)) {
            labels.push(n.name);
          }
          const children = n.children || n.subCategories || [];
          if (children.length > 0) findLabels(children);
        });
      };
      findLabels(petCategoryTree);
      if (labels.length > 0) {
        setSelectedCategoryLabels(labels);
      }
    }
  }, [selectedCategoryIds, petCategoryTree, selectedCategoryLabels]);

  const handleToggleCategorySelection = (catId: number, catName: string) => {
    setSelectedCategoryIds((prev) => {
      const exists = prev.includes(catId);
      if (exists) {
        setSelectedCategoryLabels((lbls) => lbls.filter((l) => l !== catName));
        return prev.filter((id) => id !== catId);
      } else {
        setSelectedCategoryLabels((lbls) => [...lbls, catName]);
        return [...prev, catId];
      }
    });
  };

  const renderHierarchicalTreeRows = (nodes: ICategoryNode[], depth = 0): React.ReactNode => {
    return nodes.map((node) => {
      const childNodes = node.children || node.subCategories || [];
      const isSelected = selectedCategoryIds.includes(node.id);
      const isMatchingSearch =
        !categorySearchQuery ||
        node.name.toLowerCase().includes(categorySearchQuery.toLowerCase());

      return (
        <div key={node.id} className="space-y-1">
          {isMatchingSearch && (
            <div
              onClick={() => handleToggleCategorySelection(node.id, node.name)}
              style={{ paddingLeft: `${depth * 16 + 8}px` }}
              className={`flex items-center justify-between py-1.5 px-2 rounded-lg cursor-pointer transition text-xs select-none ${
                isSelected
                  ? "bg-emerald-50 text-emerald-800 font-bold border border-emerald-200"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-4 h-4 rounded flex items-center justify-center border ${
                    isSelected
                      ? "bg-[#2F5E2E] border-[#2F5E2E] text-white"
                      : "border-slate-300 bg-white"
                  }`}
                >
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </div>
                <span>{node.name}</span>
              </div>
              {childNodes.length > 0 && (
                <span className="text-[10px] bg-slate-200/70 text-slate-500 px-1.5 py-0.5 rounded">
                  {childNodes.length} sub
                </span>
              )}
            </div>
          )}
          {childNodes.length > 0 && renderHierarchicalTreeRows(childNodes, depth + 1)}
        </div>
      );
    });
  };

  const handleMediaUpload = async (file: File, folderName: string): Promise<string | null> => {
    try {
      const response = await uploadFile({
        File: file,
        FolderName: folderName,
      }).unwrap();
      return response.fileUrl;
    } catch (error) {
      console.error(`Media processing failure [${folderName}]:`, error);
      alert("File upload failed. Please try again.");
      return null;
    }
  };

  const handleMainImageUpload = async (file: File) => {
    const url = await handleMediaUpload(file, "Pets/Primary");
    if (url) setMainImage(url);
  };

  const handleAddSubImageSlot = () => {
    setSubImages((prev) => [...prev, ""]);
  };

  const handleSubImageUploadAtIndex = async (file: File, index: number) => {
    const url = await handleMediaUpload(file, "Pets/Gallery");
    if (url) {
      setSubImages((prev) => {
        const next = [...prev];
        next[index] = url;
        return next;
      });
    }
  };

  const handleRemoveSubImageSlot = (index: number) => {
    setSubImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddVideoSlot = () => {
    setVideos((prev) => [...prev, { videoUrl: "", thumbnailUrl: "" }]);
  };

  const handleVideoUploadAtIndex = async (file: File, index: number) => {
    const url = await handleMediaUpload(file, "Pets/Videos");
    if (url) {
      setVideos((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          videoUrl: url,
          thumbnailUrl: mainImage || url,
        };
        return next;
      });
    }
  };

  const handleRemoveVideoSlot = (index: number) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormUpdateSubmit = async () => {
    if (
      !petName ||
      selectedCategoryIds.length === 0 ||
      !breed ||
      !price ||
      !description ||
      !birthDate
    ) {
      alert("Please ensure all essential parameters and category selections are filled.");
      return;
    }

    const payload: any = {
      id: Number(petId),
      microchipId: microchipId.trim() || null,
      category: selectedCategoryLabels[0] || "",
      categoryIds: selectedCategoryIds,
      petName: petName.trim(),
      breed: breed.trim(),
      price: Number(price) || 0,
      salePrice: salePrice ? Number(salePrice) : null,
      bookPrice: bookPrice ? Number(bookPrice) : null,
      productImage: mainImage,
      description: description,
      age: birthDate,
      gender: gender ? gender.label : "",
      tag: tag.trim() || null,
      petType: petType ? Number(petType.value) : PetSellType.Sell,
      petStatus: petStatus ? Number(petStatus.value) : PetStatus.Available,
      color: color || null,
      size: size || null,
      weight: weight ? Number(weight) : null,
      isVaccinated: isVaccinated,
      vaccinationDetails: isVaccinated ? vaccinationDetails : null,
      isKCIRegistered: isKCIRegistered,
      isFeatured: isFeatured,
      subImages: subImages.filter(Boolean),
      videos: videos.filter((v) => v.videoUrl),
      isActive:true,
    };

    try {
      await updatePet(payload).unwrap();
      alert("Pet profile updated successfully!");
      navigate(-1);
    } catch (err) {
      console.error("API update error:", err);
      alert("Failed to commit profile updates.");
    }
  };

  if (isFetching) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center font-nunito bg-background">
        <Loader2 className="w-9 h-9 text-mainBrandColor animate-spin stroke-[2.5]" />
        <p className="mt-3 text-xs font-bold text-gray-400 uppercase tracking-widest">
          Hydrating Pet Profile...
        </p>
      </div>
    );
  }

  if (isFetchError || !petFetchedData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-nunito bg-background">
        <div className="bg-white rounded-2xl border border-mediumgray p-6 text-center max-w-sm w-full shadow-xs">
          <AlertCircle className="w-10 h-10 text-rose-500 mx-auto" />
          <h3 className="text-lg font-black text-slate-800 mt-3">
            Sync Error Encountered
          </h3>
          <p className="text-xs font-medium text-gray-400 mt-1">
            Failed to retrieve active pet profile metrics.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 bg-logoHeading text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-xl inline-flex items-center gap-1 cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Return Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-nunito antialiased px-4 py-6 md:p-8 pb-10 max-w-5xl mx-auto space-y-6">
      {/* Global Action Processing Shield */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-mainBrandColor" />
            <span className="font-semibold text-sm">
              {isUploading ? "Uploading files..." : "Saving pet updates..."}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-gray-300/40 pl-5 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="group inline-flex items-center gap-1 text-xs font-bold text-gray-400 hover:text-logoHeading uppercase tracking-wider cursor-pointer transition-colors"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
          Back to Listings
        </button>

        <div>
          <h2 className="text-2xl font-black text-logoHeading tracking-tight">
            Edit Pet
          </h2>
        </div>
      </div>

      {/* Section 1: Profiling */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-4">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Pet Profiling Matrix
        </p>

        {/* Assigned Category Selector with Tree Branch */}
        <div className="space-y-2 pt-1">
          <label className="text-xs font-bold text-slate-600 block">
            Assigned Categories <span className="text-red-500">*</span>
          </label>
          <div className="border border-slate-200 p-3 rounded-xl bg-slate-50/40 flex flex-wrap gap-2 min-h-[40px]">
            {selectedCategoryLabels.length === 0 && (
              <span className="text-xs text-slate-400 italic">
                No categories linked. Use the tree below to pick nodes.
              </span>
            )}
            {selectedCategoryLabels.map((label, idx) => (
              <span
                key={idx}
                className="bg-emerald-50 text-emerald-800 px-2.5 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 border border-emerald-100 uppercase tracking-wide"
              >
                {label}
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() =>
                    handleToggleCategorySelection(selectedCategoryIds[idx], label)
                  }
                  className="text-rose-500 hover:text-rose-700 font-bold text-xs cursor-pointer"
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="relative w-full max-w-xs pt-1">
            <Search size={11} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search categories tree..."
              value={categorySearchQuery}
              onChange={(e) => setCategorySearchQuery(e.target.value)}
              className="w-full h-8 text-[11px] px-2 pl-7 border border-slate-200 rounded-lg outline-none focus:border-[#2F5E2E] bg-slate-50/50"
            />
          </div>

          <div className="border border-slate-200 rounded-xl p-3 max-h-52 overflow-y-auto bg-white shadow-inner">
            {isLoadingFullTree ? (
              <div className="flex items-center gap-1.5 text-slate-400 p-2 italic text-xs">
                <Loader2 size={13} className="animate-spin" /> Pulling pet categories...
              </div>
            ) : petCategoryTree.length > 0 ? (
              renderHierarchicalTreeRows(petCategoryTree)
            ) : (
              <p className="text-slate-400 italic pl-1 text-xs">
                No pet categories found.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pt-2">
          <TextInputComp
            value={microchipId}
            onChangeText={setMicrochipId}
            label="Microchip Identity ID"
            showLabel
            placeholder="Enter Microchip Number"
          />
          <TextInputComp
            value={petName}
            onChangeText={setPetName}
            label="Pet Name"
            showLabel
            showStar
            placeholder="Enter Registered Name"
          />
          <TextInputComp
            value={breed}
            onChangeText={setBreed}
            label="Breed Specification"
            showLabel
            placeholder="e.g. Golden Retriever"
            showStar
          />

          <div className="flex flex-col space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              max={new Date().toISOString().split("T")[0]}
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full border border-mediumgray bg-lightgray rounded-xl px-4 py-2.5 text-sm font-semibold transition focus:border-mainBrandColor outline-none focus:bg-white text-gray-700"
            />
          </div>

          <Dropdown
            options={genderOptions}
            value={gender}
            onChange={setGender}
            className="w-full"
            label="Biological Gender"
            showLabel
            innerClassName="!py-2.5"
            showStar
          />
          <Dropdown
            options={typeOptions}
            value={petType}
            onChange={setPetType}
            className="w-full"
            label="Listing Channel Type"
            showLabel
            innerClassName="!py-2.5"
            showStar
          />
          <Dropdown
            options={statusOptions}
            value={petStatus}
            onChange={setPetStatus}
            className="w-full"
            label="Inventory Status visibility"
            showLabel
            innerClassName="!py-2.5"
            showStar
          />
          <TextInputComp
            value={color}
            onChangeText={setColor}
            label="Coloring description"
            showLabel
            placeholder="e.g. Golden, Cream"
          />
          <TextInputComp
            value={size}
            onChangeText={setSize}
            label="Size Dimension"
            showLabel
            placeholder="e.g. Small, Medium"
          />
          <TextInputComp
            value={weight}
            onChangeText={setWeight}
            label="Weight (kg)"
            showLabel
            placeholder="e.g. 5"
          />
          <div className="md:col-span-2">
            <TextInputComp
              value={tag}
              onChangeText={setTag}
              label="Marketing Highlight Tag"
              showLabel
              placeholder="Most Active, Playful, Calm"
            />
          </div>
          <div className="md:col-span-2">
            <TextAreaComp
              value={description}
              onChangeText={setDescription}
              label="Comprehensive Pet Biography & Details"
              showLabel
              placeholder="Personality parameters list track histories..."
              showStar
            />
          </div>
        </div>
      </div>

      {/* Section 2: Health Indicators */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-4">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Additional Attributes & Health Metrics
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-3 justify-center">
            <label className="flex items-center gap-3 cursor-pointer text-sm font-bold text-gray-600">
              <input
                type="checkbox"
                checked={isVaccinated}
                onChange={(e) => setIsVaccinated(e.target.checked)}
                className="w-4 h-4 rounded text-mainBrandColor"
              />
              Is Fully Vaccinated
            </label>
            <label className="flex items-center gap-3 cursor-pointer text-sm font-bold text-gray-600">
              <input
                type="checkbox"
                checked={isKCIRegistered}
                onChange={(e) => setIsKCIRegistered(e.target.checked)}
                className="w-4 h-4 rounded text-mainBrandColor"
              />
              Is KCI Registered
            </label>
            <label className="flex items-center gap-3 cursor-pointer text-sm font-bold text-gray-600">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="w-4 h-4 rounded text-mainBrandColor"
              />
              Feature on Home Screen Banner
            </label>
          </div>
          {isVaccinated && (
            <TextAreaComp
              value={vaccinationDetails}
              onChangeText={setVaccinationDetails}
              label="Vaccination History Records Details"
              showLabel
              placeholder="List completed vaccine shots sequence tracking..."
            />
          )}
        </div>
      </div>

      {/* Section 3: Financial Metrics */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-4">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Financial Metrics Structure (AED)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextInputComp
            value={price}
            onChangeText={setPrice}
            label="Retail List Value"
            showLabel
            showStar
            placeholder="e.g. 700"
          />
          <TextInputComp
            value={salePrice}
            onChangeText={setSalePrice}
            label="Sale Price"
            showLabel
            placeholder="e.g. 550"
          />
          <TextInputComp
            value={bookPrice}
            onChangeText={setBookPrice}
            label="Booking Deposit Value"
            showLabel
            placeholder="e.g. 100"
          />
        </div>
      </div>

      {/* Section 4: Media Attachments */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-6">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Media Portfolio Attachments
        </p>

        <div className="w-full sm:w-1/3">
          <MediaUploadComp
            label="Primary Banner Image"
            value={mainImage}
            onChange={setMainImage}
            onFileSelect={handleMainImageUpload}
            showStar
          />
        </div>

        {/* Gallery Sub-Images */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-black uppercase tracking-wider text-gray-400">
              Gallery Portfolio Images
            </label>
            <button
              type="button"
              disabled={isProcessing}
              onClick={handleAddSubImageSlot}
              className="bg-logoHeading hover:opacity-90 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition cursor-pointer disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" /> Add Image
            </button>
          </div>

          {subImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-lightgray/40 border border-mediumgray rounded-2xl p-4">
              {subImages.map((imgUrl, idx) => (
                <div
                  key={idx}
                  className="relative group border border-gray-200 bg-white p-2 rounded-xl flex flex-col justify-between"
                >
                  <MediaUploadComp
                    label={`Gallery Image #${idx + 1}`}
                    value={imgUrl}
                    onChange={(val) =>
                      setSubImages((prev) => {
                        const next = [...prev];
                        next[idx] = val;
                        return next;
                      })
                    }
                    onFileSelect={(file) => handleSubImageUploadAtIndex(file, idx)}
                  />
                  <button
                    type="button"
                    disabled={isProcessing}
                    onClick={() => handleRemoveSubImageSlot(idx)}
                    className="mt-2 text-rose-600 hover:text-rose-800 text-xs font-bold flex items-center gap-1 self-end transition cursor-pointer disabled:opacity-40"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove Slot
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-mediumgray rounded-2xl text-xs text-gray-400 font-semibold italic">
              No gallery images added yet. Click "Add Image".
            </div>
          )}
        </div>

        {/* Videos */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-black uppercase tracking-wider text-gray-400">
              Profile Snippet Loop Videos
            </label>
            <button
              type="button"
              disabled={isProcessing}
              onClick={handleAddVideoSlot}
              className="bg-logoHeading hover:opacity-90 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition cursor-pointer disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" /> Add Video
            </button>
          </div>

          {videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-lightgray/40 border border-mediumgray rounded-2xl p-4">
              {videos.map((vidObj, idx) => (
                <div
                  key={idx}
                  className="relative group border border-gray-200 bg-white p-2 rounded-xl flex flex-col justify-between"
                >
                  <MediaUploadComp
                    label={`Loop Snippet #${idx + 1}`}
                    value={vidObj.videoUrl}
                    onChange={(val) =>
                      setVideos((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], videoUrl: val };
                        return next;
                      })
                    }
                    onFileSelect={(file) => handleVideoUploadAtIndex(file, idx)}
                    type="video"
                  />
                  <button
                    type="button"
                    disabled={isProcessing}
                    onClick={() => handleRemoveVideoSlot(idx)}
                    className="mt-2 text-rose-600 hover:text-rose-800 text-xs font-bold flex items-center gap-1 self-end transition cursor-pointer disabled:opacity-40"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove Slot
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-mediumgray rounded-2xl text-xs text-gray-400 font-semibold italic">
              No videos attached. Click "Add Video".
            </div>
          )}
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={() => navigate(-1)}
          disabled={isProcessing}
          className="w-full sm:w-auto text-center border border-mediumgray bg-white px-6 py-3 rounded-xl text-xs uppercase font-bold text-gray-500 hover:bg-lightgray tracking-wider transition disabled:opacity-40 cursor-pointer"
        >
          Discard Changes
        </button>
        <button
          type="button"
          onClick={handleFormUpdateSubmit}
          disabled={isProcessing}
          className="w-full sm:w-64 bg-logoHeading hover:bg-opacity-95 text-white px-6 py-3 rounded-xl text-xs uppercase font-bold tracking-wider text-center shadow-xs transition disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer"
        >
          Save Modifications
        </button>
      </div>
    </div>
  );
};

export default AdminPetEditScreen;